import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon,
  MagnifyingGlassIcon, FunnelIcon, XMarkIcon, CalendarIcon
} from '@heroicons/react/24/outline';

const Trips = () => {
  const [trips, setTrips] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    size: 10
  });
  
  // Filter states - matches API: /api/trips/filter
  const [filters, setFilters] = useState({
    driverId: '',
    vehicleId: '',
    fromDtTm: '',
    toDtTm: '',
    status: '' // 'active', 'completed', or ''
  });
  
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { loading, get, del } = useApi();
  const navigate = useNavigate();

  // Fetch dropdown data on mount
  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Fetch trips when filters or page changes
  useEffect(() => {
    fetchTrips();
  }, [filters, pageInfo.currentPage, searchTerm]);

  const fetchDropdownData = async () => {
    // Fetch active drivers for filter dropdown
    const driversRes = await get('/api/drivers/filter?active=true&size=100');
    if (driversRes.data) setDrivers(driversRes.data.content || []);

    // Fetch active vehicles for filter dropdown
    const vehiclesRes = await get('/api/vehicles/filter?active=true&size=100');
    if (vehiclesRes.data) setVehicles(vehiclesRes.data.content || []);
  };

  const fetchTrips = async () => {
    const params = new URLSearchParams();
    
    params.append('page', pageInfo.currentPage);
    params.append('size', pageInfo.size);
    
    if (filters.driverId) params.append('driverId', filters.driverId);
    if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
    if (filters.fromDtTm) params.append('fromDtTm', filters.fromDtTm);
    if (filters.toDtTm) params.append('toDtTm', filters.toDtTm);
    
    // Handle status filter
    if (filters.status === 'active') {
      params.append('endedAt', 'null');
    } else if (filters.status === 'completed') {
      params.append('endedAt', 'notnull');
    }
    
    const endpoint = `/api/trips/filter?${params.toString()}`;
    
    const { data } = await get(endpoint);
    if (data) {
      let filteredData = data.content || [];
      
      // Client-side search if searchTerm provided
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredData = filteredData.filter(trip => 
          trip.label?.toLowerCase().includes(term) ||
          trip.driver?.fullName?.toLowerCase().includes(term) ||
          trip.vehicle?.plates?.toLowerCase().includes(term) ||
          trip.vehicle?.brand?.toLowerCase().includes(term)
        );
      }
      
      setTrips(filteredData);
      setPageInfo({
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        currentPage: data.number || 0,
        size: data.size || 10
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this trip?')) {
      const { error } = await del(`/api/trips/${id}`);
      if (!error) {
        fetchTrips();
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTrips();
  };

  const clearFilters = () => {
    setFilters({
      driverId: '',
      vehicleId: '',
      fromDtTm: '',
      toDtTm: '',
      status: ''
    });
    setSearchTerm('');
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const getStatusBadge = (trip) => {
    if (trip.endedAt) {
      return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Completed</span>;
    } else {
      return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 animate-pulse">In Progress</span>;
    }
  };

  const columns = [
    { header: 'ID', accessor: 'id' },
    { header: 'Label', accessor: (row) => row.label || `Trip #${row.id}` },
    { header: 'Type', accessor: (row) => row.type || 'Standard' },
    { 
      header: 'Driver', 
      accessor: (row) => row.driver?.fullName || 'Unknown'
    },
    { 
      header: 'Vehicle', 
      accessor: (row) => row.vehicle ? `${row.vehicle.brand} - ${row.vehicle.plates}` : 'Unknown'
    },
    { 
      header: 'Started', 
      accessor: (row) => row.startedAt ? new Date(row.startedAt).toLocaleString() : '-'
    },
    { header: 'Devices', accessor: (row) => row.devices?.length || 0 },
    { 
      header: 'Status', 
      accessor: (row) => getStatusBadge(row)
    },
    { 
      header: 'Actions', 
      accessor: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/trips/${row.id}`)}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => navigate(`/trips/edit/${row.id}`)}
            className="text-green-600 hover:text-green-900"
            title="Edit"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="text-red-600 hover:text-red-900"
            title="Delete"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Trips</h1>
        <button
          onClick={() => navigate('/trips/add')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create Trip</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by label, driver name, vehicle plates..."
              className="input-field pl-10"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Search
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${
              showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {(filters.driverId || filters.vehicleId || filters.fromDtTm || filters.toDtTm || filters.status || searchTerm) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <XMarkIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </form>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <FunnelIcon className="h-4 w-4 mr-1" />
            Advanced Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Driver Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Driver
              </label>
              <select
                name="driverId"
                value={filters.driverId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Drivers</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.fullName} - {driver.mobileNumber}
                  </option>
                ))}
              </select>
            </div>

            {/* Vehicle Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Vehicle
              </label>
              <select
                name="vehicleId"
                value={filters.vehicleId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Vehicles</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.brand} - {vehicle.plates} ({vehicle.color})
                  </option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" />
                From Date
              </label>
              <input
                type="datetime-local"
                name="fromDtTm"
                value={filters.fromDtTm || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" />
                To Date
              </label>
              <input
                type="datetime-local"
                name="toDtTm"
                value={filters.toDtTm || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
            </div>
          </div>

          {/* Quick Status Filters */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2 self-center">Quick status:</span>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'active' }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.status === 'active' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
              }`}
            >
              In Progress
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: 'completed' }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.status === 'completed' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-green-100 text-green-800 hover:bg-green-200'
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.status === '' 
                  ? 'bg-gray-500 text-white' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              All Trips
            </button>
          </div>

          {/* Quick Date Filters */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2 self-center">Quick dates:</span>
            <button
              onClick={() => {
                const today = new Date();
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: yesterday.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 24 Hours
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: weekAgo.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const monthAgo = new Date();
                monthAgo.setMonth(monthAgo.getMonth() - 1);
                setFilters(prev => ({ 
                  ...prev, 
                  fromDtTm: monthAgo.toISOString().slice(0, 16),
                  toDtTm: today.toISOString().slice(0, 16)
                }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 30 Days
            </button>
          </div>
        </div>
      )}

      {/* Results count with status indicator */}
      <div className="mb-4 text-sm text-gray-600 flex justify-between items-center">
        <span>Showing {trips.length} of {pageInfo.totalElements} trips</span>
        {filters.status === 'active' && (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
            Showing In Progress Only
          </span>
        )}
        {filters.status === 'completed' && (
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
            Showing Completed Only
          </span>
        )}
      </div>

      {/* Data Table */}
      <DataTable 
        columns={columns} 
        data={trips}
        pageInfo={pageInfo}
        onPageChange={(page) => {
          setPageInfo({...pageInfo, currentPage: page});
        }}
      />
    </div>
  );
};

export default Trips;